import React from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import {
    Box,
    Checkbox,
    Dropdown,
    IconButton,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Tag,
} from 'shell/components';
import { getUserPreferredFullName, getUserPronoun } from 'utils/helper';

const getPrettyRole = (role) => {
    return role === 'student' ? 'Student' : 'Instructor';
};

const getLastActivity = (activity) => {
    if (!activity) return '--';
    return moment(activity.created_on).format('MMM D[,] YYYY');
};

const MemberList = ({
    isInCourseDashboard,
    device,
    accountUsers,
    checked,
    setChecked,
    getMemberActionOptions,
    onSelect,
}) => {
    return (
        <TableOuterBox device={device} isInCourseDashboard={isInCourseDashboard}>
            <TableInnerBox device={device} isInCourseDashboard={isInCourseDashboard}>
                <CustomTable device={device} size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ pr: 0 }}>
                                <Checkbox
                                    aria-label={'select all members'}
                                    checked={accountUsers.every((accountUser) =>
                                        checked.includes(accountUser.owner_account_id)
                                    )}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setChecked(accountUsers.map(({ owner_account_id }) => owner_account_id));
                                        } else {
                                            setChecked([]);
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Last Activity</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(accountUsers || []).map((accountUser) => (
                            <TableRow key={accountUser.owner_account_id}>
                                <TableCell sx={{ pr: 0 }}>
                                    <Checkbox
                                        aria-label={'select member'}
                                        checked={checked.includes(accountUser.owner_account_id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setChecked((current) => [...current, accountUser.owner_account_id]);
                                            } else {
                                                setChecked((current) =>
                                                    current.filter(
                                                        (ownerAccountId) =>
                                                            ownerAccountId !== accountUser.owner_account_id
                                                    )
                                                );
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell noWrap>
                                    {getUserPreferredFullName(accountUser) ? (
                                        <>
                                            {getUserPreferredFullName(accountUser)}{' '}
                                            {accountUser.gender ? `(${getUserPronoun(accountUser.gender)})` : ''}
                                        </>
                                    ) : (
                                        <>{accountUser.email}</>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <CustomBox>{accountUser.email}</CustomBox>
                                        {!accountUser.is_pending ? <Tag type={'invitation'} value={'pending'} /> : null}
                                    </Stack>
                                </TableCell>
                                <TableCell>{getPrettyRole(accountUser.role)}</TableCell>
                                <TableCell>{getLastActivity(accountUser.login_activity)}</TableCell>
                                <TableCell>
                                    <Dropdown
                                        id={`table-dropdown-${accountUser.owner_account_id}`}
                                        options={getMemberActionOptions(accountUser)}
                                        rootComponent={
                                            <IconButton
                                                aria-label={'more options'}
                                                kind={'moreHorizontal'}
                                                size={'medium'}
                                                variant={'square'}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        }
                                        onSelect={({ key }) => onSelect(key, accountUser)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </CustomTable>
            </TableInnerBox>
        </TableOuterBox>
    );
};

const TableOuterBox = styled(Box)(({ device, isInCourseDashboard, theme }) => {
    return {
        ...(device === 'mobile' && {
            margin: theme.spacing(0, -5),
            width: '100vw',
            overflowX: 'auto',
        }),
        ...(device === 'tablet' && {
            margin: theme.spacing(0, -5),
            overflowX: 'auto',
            ...(isInCourseDashboard &&
                process.env.REACT_APP_AUTH === 'PASSWORD' && {
                    margin: theme.spacing(0, -4),
                    width: 'calc(100vw - 229px - 40px - 1px)',
                }),
        }),
    };
});

const TableInnerBox = styled(Box)(({ device, isInCourseDashboard, theme }) => {
    return {
        ...(device === 'mobile' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
        }),
        ...(device === 'tablet' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
            ...(isInCourseDashboard && {
                padding: theme.spacing(0, 4),
            }),
        }),
    };
});

const CustomTable = styled(Table)(({ device, theme }) => {
    return {
        ...(device !== 'mobile' && {
            tableLayout: 'fixed',
            marginTop: theme.spacing(2),
            '& > thead > tr > th:nth-of-type(1)': {
                width: '40px',
            },
            '& > thead > tr > th:nth-of-type(2)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(3)': {},
            '& > thead > tr > th:nth-of-type(4)': {
                width: '15%',
            },
            '& > thead > tr > th:nth-of-type(5)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(6)': {
                width: '56px',
            },
            '& > tbody > tr > td:nth-of-type(1)': {
                width: '40px',
            },
            '& > tbody > tr > td:nth-of-type(2)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(3)': {},
            '& > tbody > tr > td:nth-of-type(4)': {
                width: '15%',
            },
            '& > tbody > tr > td:nth-of-type(5)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(6)': {
                width: '56px',
            },
        }),
        ...(device === 'tablet' && {
            minWidth: '50rem',
        }),
    };
});

const CustomBox = styled(Box)(() => {
    return {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    };
});

export default MemberList;
